import {
  BadRequestException,
  ConflictException,
  Injectable,
  NotFoundException,
  Optional,
} from '@nestjs/common';
import {
  CategoryListResponse,
  CategorySummary,
  CreateCategoryRequest,
  UpdateCategoryRequest,
} from '@cart/shared-types';
import { AuditService } from '../audit/audit.service';
import { CategoriesRepository } from './categories.repository';

@Injectable()
export class CategoriesService {
  constructor(
    private readonly categoriesRepository: CategoriesRepository,
    @Optional()
    private readonly auditService?: AuditService
  ) {}

  async list(parentId?: string): Promise<CategoryListResponse> {
    const items = await this.categoriesRepository.list(parentId);

    return { items, total: items.length };
  }

  async create(input: CreateCategoryRequest): Promise<CategorySummary> {
    this.validateCreateCategory(input);

    if (await this.categoriesRepository.existsBySlug(input.slug)) {
      throw new ConflictException('Category slug already exists');
    }

    const category = await this.categoriesRepository.create(input);

    await this.auditService?.logChange({
      schemaName: 'master',
      tableName: 'categories',
      recordId: category.id,
      operation: 'create',
      changedColumns: ['name', 'slug'],
      newValue: category,
      source: 'CategoriesService.create',
    });

    return category;
  }

  async update(categoryId: string, input: UpdateCategoryRequest): Promise<CategorySummary> {
    if (input.name !== undefined && !input.name.trim()) {
      throw new BadRequestException('name cannot be empty');
    }

    const category = await this.categoriesRepository.update(categoryId, input);

    if (!category) {
      throw new NotFoundException('Category not found');
    }

    await this.auditService?.logChange({
      schemaName: 'master',
      tableName: 'categories',
      recordId: category.id,
      operation: 'update',
      changedColumns: Object.keys(input),
      newValue: category,
      source: 'CategoriesService.update',
    });

    return category;
  }

  async delete(categoryId: string): Promise<void> {
    const deleted = await this.categoriesRepository.delete(categoryId);

    if (!deleted) {
      throw new NotFoundException('Category not found');
    }

    await this.auditService?.logChange({
      schemaName: 'master',
      tableName: 'categories',
      recordId: categoryId,
      operation: 'delete',
      source: 'CategoriesService.delete',
    });
  }

  private validateCreateCategory(input: CreateCategoryRequest): void {
    if (!input.name?.trim()) {
      throw new BadRequestException('name is required');
    }

    if (!input.slug?.trim()) {
      throw new BadRequestException('slug is required');
    }

    if (!/^[a-z0-9-]+$/.test(input.slug.trim())) {
      throw new BadRequestException('slug must contain only lowercase letters, numbers, and hyphens');
    }
  }
}
